import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, TrendingUp, Target, Gauge } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area,
} from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#0891b2', '#ec4899', '#78716c'];
const STATUS_COLORS = { Planned: '#3b82f6', 'In Progress': '#f59e0b', Completed: '#10b981', 'On Hold': '#78716c', Cancelled: '#ef4444' };
const MACHINE_COLORS = { Operational: '#10b981', Maintenance: '#f59e0b', 'Out Of Service': '#ef4444', Idle: '#78716c' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-border rounded-xl px-3 py-2 shadow-lg text-[12px]">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-semibold text-foreground">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function FactoryAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/factory/analytics');
        setData(res.data);
      } catch { toast.error('Failed to load analytics'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64 text-muted-foreground">Loading analytics...</div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="flex items-center justify-center h-64 text-muted-foreground">Failed to load data</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="factory-analytics-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
            <BarChart3 size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="factory-analytics-title">Production Analytics</h1>
            <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Yield trends, defect rates, utilization & consumption</p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Overall Yield</p>
              <p className="text-2xl font-bold font-outfit text-success mt-1" data-testid="overall-yield">{data.summary.overall_yield}%</p>
              <p className="text-[10px] text-muted-foreground mt-1">{data.summary.total_orders} orders</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-info uppercase tracking-[0.08em]">QC Pass Rate</p>
              <p className="text-2xl font-bold font-outfit text-info mt-1" data-testid="pass-rate">{data.summary.overall_pass_rate}%</p>
              <p className="text-[10px] text-muted-foreground mt-1">{data.summary.total_checks} checks</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Machines</p>
              <p className="text-2xl font-bold font-outfit text-cyan-600 mt-1">{data.summary.total_machines}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{data.machine_utilization.find(m => m.name === 'Operational')?.value || 0} operational</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Materials</p>
              <p className="text-2xl font-bold font-outfit text-warning mt-1">{data.summary.total_materials}</p>
              <p className="text-[10px] text-muted-foreground mt-1">tracked items</p>
            </CardContent>
          </Card>
        </div>

        {/* Row 1: Yield Trend + Order Status */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl md:col-span-2">
            <CardHeader className="pt-5 px-6 pb-2">
              <CardTitle className="text-[15px] font-outfit font-semibold text-foreground flex items-center gap-2">
                <TrendingUp size={16} className="text-success" />Yield Rate Trend
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {data.yield_trend.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-[13px]">No production data yet. Create orders to see trends.</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={data.yield_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#a8a29e' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#a8a29e' }} domain={[0, 100]} unit="%" />
                    <Tooltip content={<CustomTooltip />} />
                    <defs>
                      <linearGradient id="yieldGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="yield_rate" name="Yield %" stroke="#10b981" strokeWidth={2.5} fill="url(#yieldGradient)" dot={{ r: 4, fill: '#10b981' }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl">
            <CardHeader className="pt-5 px-6 pb-2">
              <CardTitle className="text-[15px] font-outfit font-semibold text-foreground flex items-center gap-2">
                <Target size={16} className="text-info" />Order Status
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {data.status_breakdown.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-[13px]">No orders yet</div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={data.status_breakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={3} strokeWidth={0}>
                        {data.status_breakdown.map((entry, i) => (
                          <Cell key={i} fill={STATUS_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {data.status_breakdown.map((s, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[11px]">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[s.name] || COLORS[i] }} />
                        <span className="text-muted-foreground">{s.name}: <span className="font-semibold text-foreground">{s.value}</span></span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Quality Over Time + Defect Types */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl md:col-span-2">
            <CardHeader className="pt-5 px-6 pb-2">
              <CardTitle className="text-[15px] font-outfit font-semibold text-foreground flex items-center gap-2">
                <Gauge size={16} className="text-warning" />Quality Over Time
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {data.monthly_quality.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-[13px]">No quality checks yet. Record inspections to see trends.</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.monthly_quality} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#a8a29e' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#a8a29e' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="passed" name="Passed" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="failed" name="Failed" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl">
            <CardHeader className="pt-5 px-6 pb-2">
              <CardTitle className="text-[15px] font-outfit font-semibold text-foreground">Defect Types</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {data.defect_types.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-[13px]">No defects recorded</div>
              ) : (
                <div className="space-y-3">
                  {data.defect_types.map((d, i) => {
                    const max = Math.max(...data.defect_types.map(x => x.value));
                    const pct = max > 0 ? (d.value / max) * 100 : 0;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-[12px] mb-1">
                          <span className="text-muted-foreground font-medium">{d.name}</span>
                          <span className="font-bold text-foreground">{d.value}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 3: Machine Utilization + Material Consumption */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl">
            <CardHeader className="pt-5 px-6 pb-2">
              <CardTitle className="text-[15px] font-outfit font-semibold text-foreground">Machine Utilization</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {data.machine_utilization.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-[13px]">No machines added yet</div>
              ) : (
                <div className="flex items-center gap-6">
                  <ResponsiveContainer width="55%" height={200}>
                    <PieChart>
                      <Pie data={data.machine_utilization} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={4} strokeWidth={0}>
                        {data.machine_utilization.map((entry, i) => (
                          <Cell key={i} fill={MACHINE_COLORS[entry.name] || COLORS[i]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2.5">
                    {data.machine_utilization.map((m, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: MACHINE_COLORS[m.name] || COLORS[i] }} />
                        <span className="text-[12px] text-muted-foreground">{m.name}</span>
                        <Badge className="text-[10px] bg-muted text-foreground font-bold">{m.value}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl">
            <CardHeader className="pt-5 px-6 pb-2">
              <CardTitle className="text-[15px] font-outfit font-semibold text-foreground">Material Consumption (Top 10)</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {data.material_consumption.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-[13px]">No materials tracked yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.material_consumption} layout="vertical" margin={{ left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#a8a29e' }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#78716c' }} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="Value (SR)" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
